'use client';
import React, { useEffect, useState } from 'react';

import { gql } from '@apollo/client';
import { useQueryAll } from '../apolloClient';
import { useScreenSize } from "../hooks/ScreenSizeContext";

import Toolbar from '@mui/material/Toolbar';
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import { styled } from '@mui/material/styles';
import MDButton from './MDButton/MDButtonRoot';
import MDBox from './MDBox/MDBoxRoot';
import StyledBox from './StyleBox';
import setThemeObj from './../hooks/useThemeMUI';
import MDTypographyRoot from './MDTypography/MDTypographyRoot';
import * as types from '../gql/graphql';
import { SchoolTime } from '../utils/SchoolTime';
import { Paper } from '@mui/material';

// GraphQL Query
const getStudentsQuery = gql(`
  query GetStudentsFroReport($after: Cursor, $from: Date, $to: Date) {
    fs_studentsCollection(
      filter: {and: [{EnrolementDate: {gte: $from}}, {EnrolementDate: {lte: $to}}]}
      orderBy: {EnrolementDate: DescNullsFirst}
      after: $after
    ) {
      edges {
        node {
          id
          FirstName
          LastName
          Email
          DOB
          Height
          Weight
          AddrStreet
          AddrTown
          AddrZip
          AddrState
          Mobile
          MedCondFlag
          MedCondDescr
          ExpLevel
          HasEquipment
          Goals
          Reference
          Notes
          EnrolementDate
          RegisteredBy
          RegistrationState
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`);

const getAdminsQuery = gql(`
  query GetAdminsForReport($after: Cursor) {
      fs_adminsCollection(after: $after) {
          pageInfo { endCursor startCursor hasNextPage }
          edges {
            node {
              first_name
              last_name
              uid
            }
          }
        }
    }
  `);

const Registrations = () => {
  // Set default dates: from = 1 month ago, to = today
  const today = SchoolTime.now();
  const oneMonthAgo = today.addMonths(-1);

  const [fromInput, setFromInput] = useState(oneMonthAgo.toDateTime().toISODate());
  const [toInput, setToInput] = useState(today.toDateTime().toISODate());

  const [dateFilter, setDateFilter] = useState({ from: oneMonthAgo, to: today });

  const { data: admins } = useQueryAll<types.Fs_AdminsEdge>({
    query: getAdminsQuery
  });

  const { loading, error, data } = useQueryAll<types.Fs_StudentsEdge>({
    query: getStudentsQuery,
    variables: { from: dateFilter.from.toISOLocal(), to: dateFilter.to.toISOLocal() },
  });

  const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFromInput(e.target.value);
  };

  const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setToInput(e.target.value);
  };

  const handleRunReport = () => {
    const fromDate = SchoolTime.fromISODate(fromInput);
    const toDate = SchoolTime.fromISODate(toInput);

    setDateFilter({ from: fromDate, to: toDate });
  };

  if (loading) return (<MDBox p={2}>Loading...</MDBox>);
  if (error) return (<div>Error loading students</div>);


  const students = data.map(edge => edge.node);

  const formatStudentRow = (student, admins) => {
    const formatBool = (val) => val === true ? "Yes" : val === false ? "No" : "";

    const expLevelMap = {
      1: "Beginner",
      2: "Experienced",
      3: "Intermediate",
    };

    const registrationMap = {
      10: "Signed up",
      20: "Completed waiver",
      30: "Updated profile",
    };

    const admin = admins?.find((a) => a.node.uid === student.RegisteredBy);

    return {
      id: student.id,
      FirstName: student.FirstName,
      LastName: student.LastName,
      Email: student.Email,
      DOB: student.DOB ? SchoolTime.fromISODate(student.DOB).toShortDateString() : '',
      Height: student.Height,
      Weight: student.Weight,
      Mobile: student.Mobile,
      MedCondFlag: formatBool(student.MedCondFlag),
      MedCondDescr: student.MedCondDescr,
      ExpLevel: expLevelMap[student.ExpLevel] || '',
      HasEquipment: formatBool(student.HasEquipment),
      Goals: student.Goals,
      Reference: student.Reference,
      Notes: student.Notes,
      EnrolementDate: student.EnrolementDate ? SchoolTime.fromISODate(student.EnrolementDate).toShortDateString() : '',
      RegistrationState: registrationMap[student.RegistrationState] || 'Not signed up',
      RegisteredBy: student.RegisteredBy ? `${admin.node.first_name} ${admin.node.last_name}` : '',
    };
  };

  const tableHeaders = [
    'User ID', 'First Name', 'Last Name', 'E-Mail', 'DOB', 'Height', 'Weight',
    'Mobile', 'Med Condition', 'Med Description', 'Experience', 'Equipment',
    'Goals', 'Reference', 'Ref Details', 'Enrolment Date', 'Waiver', 'Registered By'
  ];

  const rowKeys = [
    'id', 'FirstName', 'LastName', 'Email', 'DOB', 'Height', 'Weight',
    'Mobile', 'MedCondFlag', 'MedCondDescr', 'ExpLevel', 'HasEquipment',
    'Goals', 'Reference', 'Notes', 'EnrolementDate', 'RegistrationState', 'RegisteredBy'
  ];

  const downloadCSV = (rows) => {
    const headers = tableHeaders.join(",");
    const csvRows = rows.map(row =>
      rowKeys.map(key => {
        const cell = row[key];
        // Escape quotes
        const safe = typeof cell === 'string' ? `"${cell.replace(/"/g, '""')}"` : cell;
        return safe;
      }).join(",")
    );
  
    const csvContent = [headers, ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
  
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `student_report_${fromInput}_to_${toInput}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  


  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Card>
          <MDBox component="main" px={4} py={4}>

            {/* Date Filter */}
            <StyledBox theme={setThemeObj} sx={{ marginBottom: 2 }}>
              <Grid container alignItems="flex-end" justifyContent="space-between">
                <Grid item sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="From:"
                    type="date"
                    value={fromInput}
                    onChange={handleFromDateChange}
                    InputLabelProps={{ shrink: true }}
                    margin="normal"
                  />

                  <TextField
                    label="To:"
                    type="date"
                    value={toInput}
                    onChange={handleToDateChange}
                    InputLabelProps={{ shrink: true }}
                    margin="normal"
                  />

                  <MDButton
                    variant="contained"
                    color="primary"
                    onClick={handleRunReport}
                    sx={{ height: '56px', whiteSpace: 'nowrap' }}
                  >
                    Run Report
                  </MDButton>
                </Grid>

                <Grid item>
                  <MDButton
                    variant="contained"
                    color="secondary"
                    onClick={() => downloadCSV(students.map(s => formatStudentRow(s, admins)))}
                    sx={{ height: '56px', whiteSpace: 'nowrap' }}
                  >
                    Download
                  </MDButton>
                </Grid>
              </Grid>
            </StyledBox>


            {/* Students Table */}
            <MDBox sx={{ maxHeight: '800px', overflowY: 'auto' }}>
              <MDBox component="table" sx={{ borderSpacing: "0px", width: "100%" }}>
                <MDBox
                  component="thead"
                  ownerState={{ variant: "contained", bgColor: "light" }}
                  sx={{
                    '& td': { fontSize: '12px' },
                    position: 'sticky',
                    top: 0,
                    backgroundColor: 'white',
                    zIndex: 1,
                  }}
                  borderRadius={2}
                >
                  <MDBox component="tr">
                    {tableHeaders.map((header) => (
                      <TableCell key={header}>{header}</TableCell>
                    ))}
                  </MDBox>
                </MDBox>

                <TableBody sx={{ '& td': { fontSize: '12px' } }}>
                  {students.map((student) => {
                    const formatted = formatStudentRow(student, admins);
                    return (
                      <TableRow key={student.id}>
                        {rowKeys.map((key) => (
                          <TableCell key={key}>{formatted[key]}</TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </MDBox>
            </MDBox>

          </MDBox>
        </Card>
      </Grid>
    </Grid>
  );
};

export default Registrations;
